import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";
import SwissBadge from "@/components/SwissBadge";

const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "https://www.easygutachter.de";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", work_type: "selbststaendig" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedAssignment, setAcceptedAssignment] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Bitte AGB und Nutzungsbedingungen akzeptieren.");
      return;
    }
    if (!acceptedAssignment) {
      toast.error("Bitte Sicherungsabtretung & Datenschutz akzeptieren.");
      return;
    }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Konto erstellt. Los geht's!");
      navigate(form.work_type === "provision" ? "/provision" : "/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="flex items-center justify-between gap-3">
            <a href={WEBSITE_URL} className="inline-flex items-center gap-3 text-sm text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="back-to-website">
              <ArrowLeft className="h-4 w-4" /> Zurück zur Website
            </a>
            <a
              href={`${WEBSITE_URL}/gutachter-werden`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="register-more-info-btn"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#12A9E8] border border-[#12A9E8]/30 bg-[#12A9E8]/5 hover:bg-[#12A9E8]/10 rounded-full px-4 py-1.5 transition-colors shrink-0"
            >
              <Info className="h-4 w-4" /> Mehr Infos zur Website
            </a>
          </div>
          <a href={WEBSITE_URL} className="flex items-center cursor-pointer hover:opacity-80 transition-opacity" data-testid="register-brand">
            <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="h-20 w-auto select-none" draggable={false} />
          </a>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Konto erstellen</h1>
            <p className="text-zinc-500 text-sm mt-2" data-testid="register-subline">
              {form.work_type === "provision"
                ? "100 % kostenlos für Provisionspartner — du verdienst 200 € pro Auftrag."
                : "2 Tage kostenlos testen. Danach alle 3 Monate kündbar."}
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Voller Name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="register-name-input" />
            </div>
            <div>
              <Label>Wie möchtest du arbeiten?</Label>
              <Select value={form.work_type} onValueChange={(v) => setForm((f) => ({ ...f, work_type: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="register-worktype-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="selbststaendig" data-testid="register-worktype-selbststaendig">
                    Selbstständig — eigene Gutachten erstellen
                  </SelectItem>
                  <SelectItem value="provision" data-testid="register-worktype-provision">
                    Nicht selbstständig — Aufträge einreichen (200 € Provision)
                  </SelectItem>
                </SelectContent>
              </Select>
              {form.work_type === "provision" && (
                <p className="text-xs text-zinc-500 mt-1.5 bg-cyan-50/60 border border-cyan-100 rounded-lg px-3 py-2" data-testid="register-provision-hint">
                  Du machst nur Fotos vom Schaden und lädst sie bei uns hoch — wir erstellen das
                  Gutachten in unserem Namen und du erhältst <strong>200 € pro Auftrag</strong> nach
                  Zahlungseingang.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="email">E-Mail</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Passwort (min. 8 Zeichen)</Label>
              <Input id="password" type="password" required value={form.password} onChange={set("password")} className="mt-1.5" data-testid="register-password-input" />
            </div>
            <div className="space-y-3 pt-1">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="register-terms-label">
                <Checkbox
                  id="accept-terms"
                  checked={acceptedTerms}
                  onCheckedChange={(v) => setAcceptedTerms(Boolean(v))}
                  className="mt-0.5"
                  data-testid="register-accept-terms-checkbox"
                />
                <span className="text-sm text-zinc-700 leading-relaxed">
                  Hiermit akzeptiere ich die{" "}
                  <Link to="/legal/agb" target="_blank" className="text-[#12A9E8] hover:underline" data-testid="register-link-agb">
                    Nutzungsbedingungen und die AGB
                  </Link>
                  .
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="register-assignment-label">
                <Checkbox
                  id="accept-assignment"
                  checked={acceptedAssignment}
                  onCheckedChange={(v) => setAcceptedAssignment(Boolean(v))}
                  className="mt-0.5"
                  data-testid="register-accept-assignment-checkbox"
                />
                <span className="text-sm text-zinc-700 leading-relaxed">
                  Hiermit akzeptiere ich die{" "}
                  <Link to="/legal/sicherungsabtretung" target="_blank" className="text-[#12A9E8] hover:underline" data-testid="register-link-sicherungsabtretung">
                    Sicherungsabtretung
                  </Link>
                  {" "}inkl.{" "}
                  <Link to="/legal/datenschutz" target="_blank" className="text-[#12A9E8] hover:underline" data-testid="register-link-datenschutz">
                    Datenschutz
                  </Link>
                  .
                </span>
              </label>
            </div>
            <Button type="submit" className="w-full brand-gradient-bg text-white" disabled={loading || !acceptedTerms || !acceptedAssignment} data-testid="register-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Konto erstellen
            </Button>
          </form>
          <div className="text-sm text-center text-zinc-500">
            Bereits registriert? <Link to="/login" className="text-[#12A9E8] font-medium hover:underline">Anmelden</Link>
          </div>
          <div className="text-sm text-center text-zinc-500" data-testid="register-support-phone">
            Fragen? Support: <a href="tel:+491785325630" className="text-[#12A9E8] font-medium hover:underline">+49 178 5325630</a>
          </div>
          <div className="flex justify-center pt-1">
            <SwissBadge />
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative brand-gradient-bg">
        <div className="absolute inset-0 grain" />
        <div className="relative h-full flex items-end p-12">
          <div className="max-w-md text-white">
            <div className="text-xs uppercase tracking-[0.22em] font-bold opacity-80">Willkommen</div>
            <div className="font-display text-4xl font-bold tracking-tight leading-[1.05] mt-2">
              Schreiben Sie Gutachten, keine Texte.
            </div>
            <div className="mt-4 text-white/80">Automatisiert, juristisch präzise, DSGVO-konform.</div>
            <div className="mt-6">
              <SwissBadge variant="dark" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
